/**
 * Universal Logger for Harry Playground
 * 
 * Provides consistent logging across client and server with correlation IDs
 * and support for specialized logging formats (Auth, Role, Access, Network, etc.)
 * 
 * ARCHITECTURE NOTE ON DUAL LOGGING:
 * The logger uses captureLog() directly instead of relying on the console interceptor
 * to avoid duplicate log entries. The console interceptor (in activity-logger.ts)
 * captures console.* calls from third-party code and non-logger sources.
 * When isRecording(), captureLog() is the ONLY path — the console interceptor
 * checks a flag (_fromLogger) to skip logger-originated calls.
 */

import { getCorrelationId, initializeCorrelationId } from './correlation-id';
import { isRecording, isDiagnosticCaptureEnabled, captureLog, triggerErrorReportIfSystemWide, isSystemWideAutoRecording, setErrorReportInProgress, serializeForLog } from '@/lib/activity-logger';
import { LogLevel, LogCategory, LOG_LEVEL_PRIORITY } from './types';
import type { LogContext } from './types';

export { LogLevel, LogCategory };
export type { LogContext };

interface FormattedLog {
    timestamp: string;
    level: LogLevel;
    correlationId: string;
    category?: LogCategory;
    message: string;
    context?: any;
}

class Logger {
    private context: LogContext = {};
    private level: LogLevel = LogLevel.INFO;
    private pollingInterval: NodeJS.Timeout | null = null;
    private lastPolledLevel: LogLevel | null = null;

    constructor() {
        // Auto-initialize correlation ID immediately
        // This ensures the first log line always has an ID
        initializeCorrelationId();
        
        // Initialize log level from environment variable
        this.initializeLogLevel();
        
        // Start polling after a short delay (client-side only)
        if (typeof window !== 'undefined') {
            setTimeout(() => {
                this.startPolling();
            }, 1000);
        }
    }

    /**
     * Initialize log level from environment variable
     */
    private initializeLogLevel() {
        // Check for environment variable first (client-side: import.meta.env, server-side: process.env)
        const envLevel = typeof window !== 'undefined' 
            ? (import.meta.env?.VITE_LOG_LEVEL as string)
            : (process.env?.LOG_LEVEL as string);
        
        if (envLevel) {
            const level = this.parseLogLevel(envLevel);
            if (level !== null) {
                this.level = level;
                this.lastPolledLevel = level;
                return;
            }
        }
        
        // Default to INFO if no environment variable
        this.level = LogLevel.INFO;
        this.lastPolledLevel = LogLevel.INFO;
    }

    /**
     * Parse log level string to LogLevel enum
     */
    private parseLogLevel(level: string): LogLevel | null {
        const normalized = level.toLowerCase().trim();
        switch (normalized) {
            case 'debug': return LogLevel.DEBUG;
            case 'info': return LogLevel.INFO;
            case 'warn': return LogLevel.WARN;
            case 'error': return LogLevel.ERROR;
            default: return null;
        }
    }

    /**
     * Start polling for log level changes from Firestore
     * Polls every 30 seconds to check for updates
     */
    private startPolling() {
        // Only poll in browser environment
        if (typeof window === 'undefined') return;
        
        // Initial check
        this.checkLogLevelFromSettings();
        
        // Poll every 30 seconds
        this.pollingInterval = setInterval(() => {
            this.checkLogLevelFromSettings();
        }, 30000);
    }

    /**
     * Check log level from Firestore settings and update if changed
     */
    private async checkLogLevelFromSettings() {
        try {
            // Dynamically import to avoid circular dependencies
            const { getClientLogLevel } = await import('@/components/feedback-button/lib/env-service');
            const levelStr = await getClientLogLevel();
            const newLevel = this.parseLogLevel(levelStr);
            
            if (newLevel !== null && newLevel !== this.lastPolledLevel) {
                this.setLevel(newLevel);
                this.lastPolledLevel = newLevel;
            }
        } catch (error) {
            // Silently fail - don't spam console if settings can't be loaded
        }
    }

    /**
     * Stop polling for log level changes
     */
    stopPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    /**
     * Set the current log level
     */
    setLevel(level: LogLevel) {
        this.level = level;
    }

    /**
     * Get the current log level
     */
    getLevel(): LogLevel {
        return this.level;
    }

    /**
     * Check if a log level should be emitted based on current level
     */
    private shouldLog(level: LogLevel): boolean {
        return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.level];
    }

    /**
     * Set persistent context that will be included in all subsequent logs
     */
    setContext(context: Partial<LogContext>) {
        this.context = { ...this.context, ...context };
    }

    /**
     * Clear all context
     */
    clearContext() {
        this.context = {};
    }

    /**
     * Format log message with correlation ID and context
     */
    private formatMessage(level: LogLevel, message: string, meta?: any): FormattedLog {
        const correlationId = getCorrelationId();
        const timestamp = new Date().toISOString();

        return {
            timestamp,
            level,
            correlationId,
            category: meta?.category || this.context.category,
            message,
            context: {
                ...this.context,
                ...meta
            }
        };
    }

    /**
     * Generate console prefix with correlation ID and optional category
     */
    private getPrefix(correlationId: string, category?: LogCategory): string {
        const categoryPrefix = category ? `[${category.toUpperCase()}]` : '';
        return `[${correlationId}]${categoryPrefix}`;
    }

    /**
     * Serialize meta for log capture, handling Error objects properly
     */
    private serializeMeta(meta: any): string {
        if (!meta) return '';
        return serializeForLog(meta);
    }

    /**
     * Debug level logging
     */
    debug(message: string, meta?: any) {
        if (!this.shouldLog(LogLevel.DEBUG)) return;
        const formatted = this.formatMessage(LogLevel.DEBUG, message, meta);
        const prefix = this.getPrefix(formatted.correlationId, formatted.category);
        // Use _fromLogger flag so console interceptor skips this call (avoids duplicate capture)
        (console as any)._fromLogger = true;
        console.log(`${prefix} ${message}`, meta || '');
        (console as any)._fromLogger = false;
        if (isRecording() || isDiagnosticCaptureEnabled()) captureLog('log', `${prefix} ${message} ${meta ? this.serializeMeta(meta) : ''}`, formatted.category as any);
    }

    /**
     * Info level logging
     */
    info(message: string, meta?: any) {
        if (!this.shouldLog(LogLevel.INFO)) return;
        const formatted = this.formatMessage(LogLevel.INFO, message, meta);
        const prefix = this.getPrefix(formatted.correlationId, formatted.category);
        (console as any)._fromLogger = true;
        console.info(`${prefix} ${message}`, meta || '');
        (console as any)._fromLogger = false;
        if (isRecording() || isDiagnosticCaptureEnabled()) captureLog('info', `${prefix} ${message} ${meta ? this.serializeMeta(meta) : ''}`, formatted.category as any);
    }

    /**
     * Warning level logging
     */
    warn(message: string, meta?: any) {
        if (!this.shouldLog(LogLevel.WARN)) return;
        const formatted = this.formatMessage(LogLevel.WARN, message, meta);
        const prefix = this.getPrefix(formatted.correlationId, formatted.category);
        (console as any)._fromLogger = true;
        console.warn(`${prefix} ⚠️ ${message}`, meta || '');
        (console as any)._fromLogger = false;
        if (isRecording() || isDiagnosticCaptureEnabled()) captureLog('warn', `${prefix} ⚠️ ${message} ${meta ? this.serializeMeta(meta) : ''}`, formatted.category as any);
    }

    /**
     * Error level logging
     */
    error(message: string, meta?: any) {
        if (!this.shouldLog(LogLevel.ERROR)) return;
        const formatted = this.formatMessage(LogLevel.ERROR, message, meta);
        const prefix = this.getPrefix(formatted.correlationId, formatted.category);
        (console as any)._fromLogger = true;
        console.error(`${prefix} ❌ ${message}`, meta || '');
        (console as any)._fromLogger = false;
        // Set flag before captureLog so AuthDebugMonitor listener skips (logger handles submit)
        if (isSystemWideAutoRecording()) setErrorReportInProgress(true);
        if (isRecording() || isDiagnosticCaptureEnabled()) captureLog('error', `${prefix} ❌ ${message} ${meta ? this.serializeMeta(meta) : ''}`, formatted.category as any);
        if (isSystemWideAutoRecording()) {
            setErrorReportInProgress(false);
            triggerErrorReportIfSystemWide();
        }
    }

    /**
     * Specialized logging methods for common patterns
     */

    /**
     * Log authentication events
     */
    auth(message: string, meta?: any) {
        this.info(message, { ...meta, category: LogCategory.AUTH });
    }

    /**
     * Log role-related events
     */
    role(message: string, meta?: any) {
        this.info(message, { ...meta, category: LogCategory.ROLE });
    }

    /**
     * Log access control events
     */
    access(message: string, meta?: any) {
        this.info(message, { ...meta, category: LogCategory.ACCESS });
    }

    /**
     * Log network/API requests
     */
    network(message: string, meta?: any) {
        this.info(message, { ...meta, category: LogCategory.NETWORK });
    }

    /**
     * Log UI interactions
     */
    ui(message: string, meta?: any) {
        this.debug(message, { ...meta, category: LogCategory.UI });
    }

    /**
     * Log API endpoint calls
     */
    api(method: string, endpoint: string, meta?: any) {
        this.info(`${method} ${endpoint}`, { ...meta, category: LogCategory.API });
    }

    /**
     * LogDatabase operations
     */
    db(operation: string, collection: string, meta?: any) {
        this.debug(`DB ${operation}: ${collection}`, { ...meta, category: LogCategory.DB });
    }

    /**
     * Log camera/media device events (VisioMeter, SOP intake)
     */
    camera(message: string, meta?: any) {
        this.info(message, { ...meta, category: LogCategory.CAMERA });
    }
}

// Export singleton instance
export const logger = new Logger();
export default logger;
